import React, { useState } from 'react';
import { Table, Button, Select, Modal, Form, Input, InputNumber, App, Typography } from 'antd';
import { PlusOutlined, MailOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import WelcomeBanner from '../../Components/common/WelcomeBanner';
import { LoadingState, ApiErrorState, EmptyState } from '../../Components/common/StateViews';
import StatusTag from '../../Components/common/StatusTag';
import useApiData from '../../Hooks/useApiData';
import cioService from '../../Services/cioService';
import {
  OnboardingInvitationStatus,
  OnboardingInvitationStatusLabels,
  OnboardingInvitationStatusColors,
  optionsFromLabels,
} from '../../Constants/enums';
import { formatDateTime } from '../../Utils/format';
import { toApiError } from '../../api/axiosClient';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...optionsFromLabels(OnboardingInvitationStatusLabels),
];

const Invitations = () => {
  const { message } = App.useApp();
  const [status, setStatus] = useState('');
  const { data, loading, error, reload } = useApiData(
    () => cioService.getInvitations(status),
    [status]
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form] = Form.useForm();

  const rows = Array.isArray(data) ? data : data?.items || [];

  const onSend = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    setSending(true);
    try {
      const created = await cioService.createInvitation(values);
      message.success('Onboarding invitation sent.');
      setModalOpen(false);
      form.resetFields();
      // Flow C: after sending, show the Sent (status=0) tracking view.
      setStatus(OnboardingInvitationStatus.Sent);
      reload();
      if (created?.formUrl) {
        Modal.success({
          title: 'Invitation created',
          content: (
            <div>
              <p>The onboarding email has been queued. Shareable link:</p>
              <Typography.Paragraph copyable={{ text: created.formUrl }} style={{ marginBottom: 0 }}>
                {created.formUrl}
              </Typography.Paragraph>
            </div>
          ),
        });
      }
    } catch (err) {
      const { status: code, message: msg } = toApiError(err);
      message.error(code === 409 ? msg || 'This organization already has an invitation.' : msg);
    } finally {
      setSending(false);
    }
  };

  const columns = [
    { title: 'Organization', dataIndex: 'organizationName' },
    { title: 'Contact Email', dataIndex: 'contactEmail' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v) => (
        <StatusTag
          value={v}
          labels={OnboardingInvitationStatusLabels}
          colors={OnboardingInvitationStatusColors}
        />
      ),
    },
    { title: 'Expires', dataIndex: 'expiresAtUtc', render: formatDateTime },
    { title: 'Submitted', dataIndex: 'submittedAtUtc', render: formatDateTime },
    {
      title: 'Customer',
      dataIndex: 'clientId',
      render: (id) => (id ? <Link to={`/cio/customers/${id}`}>View customer</Link> : '-'),
    },
  ];

  return (
    <div>
      <WelcomeBanner
        title="Onboarding Invitations"
        subtitle="Send and track customer onboarding links"
        icon={<MailOutlined />}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            Send Onboarding Invitation
          </Button>
        }
      />

      <div style={{ marginBottom: 16 }}>
        <Select
          value={status}
          onChange={setStatus}
          options={STATUS_OPTIONS}
          style={{ width: 200 }}
        />
      </div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ApiErrorState error={error} onRetry={reload} />
      ) : rows.length === 0 ? (
        <EmptyState description="No invitations for this filter." />
      ) : (
        <Table rowKey="id" columns={columns} dataSource={rows} pagination={{ pageSize: 10 }} scroll={{ x: 'max-content' }} />
      )}

      <Modal
        title="Send Onboarding Invitation"
        open={modalOpen}
        onOk={onSend}
        onCancel={() => setModalOpen(false)}
        confirmLoading={sending}
        okText="Send invitation"
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark="optional" initialValues={{ expiresInDays: 7 }}>
          <Form.Item name="organizationName" label="Organization name" rules={[{ required: true }]}>
            <Input placeholder="e.g. True North" />
          </Form.Item>
          <Form.Item
            name="contactEmail"
            label="Customer contact email"
            rules={[{ required: true }, { type: 'email', message: 'Enter a valid email' }]}
          >
            <Input placeholder="contact@customer.com" />
          </Form.Item>
          <Form.Item name="expiresInDays" label="Expires in (days)" rules={[{ required: true }]}>
            <InputNumber min={1} max={60} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Invitations;
